import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LockClosed20Regular,
  Person20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import logoAja2Light from "../../assets/logo.png";
import logoAja2Dark from "../../assets/logodark.png";
import { useAuth } from "../../auth/AuthContext";

export function LoginView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isChecking, login } = useAuth();
  const from = location.state?.from?.pathname ?? "/dashboard";
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark/light mode
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    checkTheme();

    if (typeof MutationObserver === "undefined") return;

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isChecking && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [from, isAuthenticated, isChecking, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Authenticate via API
    try {
      const response = await login(email, password);
      if (!response?.ok) {
        throw new Error('Email ou password incorretos');
      }
      navigate(from, { replace: true });
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        setError('Email ou password incorretos');
        return;
      }
      setError(
        err instanceof Error
          ? err.message
          : 'Erro ao iniciar sessão',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-background">
      {/* Auth Card */}
      <div 
        className="w-full max-w-[420px] bg-card shadow-[var(--elevation-sm)] p-8"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        {/* Logo and Title */}
        <div className="flex flex-col items-center mb-8">
          {/* Logo */}
          <img 
            src={isDarkMode ? logoAja2Dark : logoAja2Light}
            alt="aja2 - Projectos & Construções" 
            className="h-16 object-contain mb-4"
          />

          {/* Title */}
          <h1 className="text-card-foreground mb-1 whitespace-nowrap text-[32px] font-bold">
            Gestão de Obras com IA
          </h1>
          <p className="text-[var(--muted-foreground)]">
            Inicie sessão para continuar
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div className="space-y-2">
            <label 
              htmlFor="email" 
              className="block text-card-foreground"
            >
              Email
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]">
                <Person20Regular />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                style={{ borderRadius: 'var(--radius)' }}
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label 
              htmlFor="password" 
              className="block text-card-foreground"
            >
              Password
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]">
                <LockClosed20Regular />
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="w-full h-10 pl-10 pr-3 bg-[var(--input-background)] border border-[var(--border)] text-card-foreground placeholder:text-[var(--placeholder-text)] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:border-[var(--ring)] focus:ring-2 focus:ring-[var(--ring)] focus:ring-opacity-20"
                style={{ borderRadius: 'var(--radius)' }}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="flex items-start gap-2 p-3 bg-[var(--accent)] border border-[var(--destructive)]"
              style={{ borderRadius: 'var(--radius)' }}
            >
              <Warning20Regular className="text-[var(--destructive)] flex-shrink-0 mt-0.5" />
              <p className="text-[var(--destructive)]">
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full h-10 bg-[var(--primary)] text-[var(--primary-foreground)] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:opacity-80"
            style={{ borderRadius: 'var(--radius-button)' }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg 
                  className="animate-spin h-4 w-4" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                A iniciar sessão...
              </span>
            ) : (
              'Iniciar sessão'
            )}
          </button>

          {/* Forgot Password Link */}
          <div className="text-center">
            <button
              type="button"
              disabled={isLoading}
              className="text-[var(--primary)] hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              onClick={() => {
                navigate('/auth/forgot-password');
              }}
            >
              Esqueceu-se da password?
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
